/**
 * In-memory MOCK of the mandate, library and proposal APIs.
 *
 * Used only when VITE_USE_MOCK_API=true (see src/api/client.ts and
 * src/api/mockData.ts). It exists so the wizard, library and proposal screens can
 * be built and played in Playwright before the real endpoints exist; it is NOT
 * a specification of the server. The intended endpoints are in API_CONTRACT.md.
 * A real backend replaces this by answering the same paths with the same shapes.
 *
 * The wire SHAPES are the real ones (program/src/strategy_library.rs,
 * proposals.rs, api/library_api.rs, api/proposals_api.rs); only the values differ.
 *
 * What is faked here, so nobody mistakes it for the real thing:
 * - the library entries and every number in them (placeholders, not curated);
 * - the agent: `POST /api/proposals` runs a fixed, deterministic selection rule
 *   over the mock library, with fixed statistics standing in for return history.
 *   No model is called and nothing costs money. Like the real builder it answers
 *   409 `no_eligible_strategies` (with every candidate's reasons) when nothing fits;
 * - the broker account and its equity;
 * - the body hash (a labelled non-cryptographic stand-in for SHA-256);
 * - signing, which always succeeds for the one mock user.
 * What is REAL: the mandate is validated by validate.ts, the same rules as the
 * Rust validator, so a bad mandate is rejected here exactly as the server will.
 *
 * State lives in module memory and is mirrored to localStorage so a page reload
 * keeps it. A fresh browser (or a Playwright test) starts from the seed: one
 * active mandate, the mock library and one ready proposal, so every screen has
 * something to show.
 */

import type { MandateAccountContext, MandateBody, MandatePortfolioProvisioning, MandateRecord, Money, Violation } from './types';
import type {
  Eligibility,
  ExpectedRange,
  LibraryEntry,
  LibraryEntryView,
  LibraryResponse,
  Reason,
  ReasonBlocks,
  ReasonCode,
} from './library';
import { paperBlockingReasons, primaryRange } from './library';
import type {
  AlternativeRejected,
  ApproveResponse,
  LadderReplay,
  Plan,
  Proposal,
  ProposalRefusal,
  ProposalRefusalCode,
  ProposalSleeve,
  ReplicationCheck,
  ReplicationCheckItem,
} from './proposal';
import { REQUIRED_ACK_DOCS, validateMandateJson, compareDec, parseDecimal } from './validate';
import { CURRENT_DOC_VERSION, actionLabel, formToBody, formatUsd, templateForm } from './form';

/** An error the mock API raises; client.ts turns it into an ApiClientError. */
export class MockApiError extends Error {
  status: number;
  details?: unknown;
  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.name = 'MockApiError';
    this.status = status;
    this.details = details;
  }
}

/** True for endpoints this module answers. */
export function isMandateMockEndpoint(endpoint: string): boolean {
  return (
    endpoint.startsWith('/api/mandates') ||
    endpoint.startsWith('/api/library') ||
    endpoint.startsWith('/api/proposals')
  );
}

// ---------------------------------------------------------------------------
// Mock library (illustrative placeholders, see the header). The SHAPE is the
// real wire shape of program/src/strategy_library.rs; the values are not.
// ---------------------------------------------------------------------------

const MOCK_SOURCE =
  'Illustrative placeholder for the mock API: not a published figure and not curated.';
const MOCK_RANGE_NOTE = 'Mock figure. The real library states where each number came from and how far to trust it.';
const MOCK_VERIFICATION_NOTE =
  'Mock entry: nothing was actually verified. The label is a fixture so the demo can show each state.';

export const MOCK_LIBRARY: LibraryEntry[] = [
  {
    id: 'etf-trend',
    version: 1,
    name: 'ETF trend following (Faber)',
    sources: [
      'Faber, M. (2007). A Quantitative Approach to Tactical Asset Allocation. The Journal of Wealth Management.',
    ],
    rule_text:
      'At each month-end, hold each of SPY, EFA, IEF, VNQ and DBC at an equal 20% weight if its price is above its 10-month simple moving average. Otherwise keep that 20% in cash.',
    reference_implementation: 'faber_gtaa_10m_sma 0.1.0 (mock reference; nothing is pinned).',
    instruments: ['SPY', 'EFA', 'IEF', 'VNQ', 'DBC'],
    requirements: {
      needs_shorting: false,
      min_leverage_gross: 1,
      gross_exposure_reference: null,
      needs_portfolio_vol_target: false,
      max_abs_weight_per_instrument: 0.2,
      cross_asset_rebalance: 'preferred',
      asset_classes: ['us_etf'],
      venues_supported: ['alpaca'],
      min_history_years: 0.85,
      notes: ['Very liquid ETFs; capacity is not a limit at startup-fund size.'],
    },
    rebalance: { kind: 'monthly', detail: 'At month-end close (last available trading day of the calendar month).' },
    parameters: { sma_month_ends: 10, weight_per_etf: 0.2 },
    expected_ranges: [
      { metric: 'sharpe_net', low: 0.4, high: 0.9, role: 'performance_band', source: MOCK_SOURCE, note: MOCK_RANGE_NOTE },
      { metric: 'max_drawdown', low: null, high: 0.25, role: 'performance_band', source: MOCK_SOURCE, note: MOCK_RANGE_NOTE },
    ],
    backtest_window: { start: '2016-01-01', end: '2026-09-21', max_observed_drawdown: 0.34 },
    known_deviations: [],
    failure_regimes: [
      'Choppy markets that flip a signal on and off (false signals cost a little each time).',
      'Fast V-shaped rebounds right after a sell signal.',
    ],
    cost_profile: {
      turnover_per_year_estimate: null,
      signal_flips_per_year_shadow: null,
      note: 'A few trades a month across five ETFs. Costs are small at ETF spreads.',
    },
    evidence: 'replicated_on_platform',
    last_verified: '2026-09-21',
    verification_note: MOCK_VERIFICATION_NOTE,
    retirement_rule:
      'Flag for review if the live tracking error against the reference exceeds its tolerance for two months running.',
    evidence_declared: 'replicated_on_platform',
    evidence_basis: 'declared',
    blockers: [],
    admission: { status: 'admitted' },
    verification_kind: 'human',
  },
  {
    id: 'fx-momentum',
    version: 1,
    name: 'FX momentum (long/short currencies)',
    sources: [
      'Menkhoff, L., Sarno, L., Schmeling, M. and Schrimpf, A. (2012). Currency momentum strategies. Journal of Financial Economics.',
    ],
    rule_text:
      'Each month, rank the major currencies against the US dollar by their past 3-month return. Go long the top third and short the bottom third at equal weights, and hold for one month.',
    reference_implementation: 'fx_momentum_3m_ls 0.1.0 (mock reference; nothing is pinned).',
    instruments: ['EUR/USD', 'GBP/USD', 'AUD/USD', 'NZD/USD', 'USD/JPY', 'USD/CHF', 'USD/CAD'],
    requirements: {
      needs_shorting: true,
      min_leverage_gross: 2,
      gross_exposure_reference: null,
      needs_portfolio_vol_target: false,
      max_abs_weight_per_instrument: null,
      cross_asset_rebalance: 'preferred',
      asset_classes: ['fx'],
      venues_supported: ['oanda'],
      min_history_years: 0.5,
      notes: ['Major pairs are liquid; capacity is not a limit at startup-fund size.'],
    },
    rebalance: { kind: 'monthly', detail: 'First trading day of each month.' },
    parameters: { lookback_months: 3, holding_months: 1 },
    expected_ranges: [
      { metric: 'sharpe_net', low: 0.2, high: 0.6, role: 'performance_band', source: MOCK_SOURCE, note: MOCK_RANGE_NOTE },
      { metric: 'sharpe', low: -2, high: 2, role: 'integrity_bound', source: MOCK_SOURCE, note: MOCK_RANGE_NOTE },
    ],
    backtest_window: { start: '2010-01-01', end: '2026-09-21', max_observed_drawdown: 0.22 },
    known_deviations: [],
    failure_regimes: [
      'Sudden risk-off reversals, when the crowded winners fall together (momentum crashes).',
      'Long stretches with no clear currency trends.',
    ],
    cost_profile: {
      turnover_per_year_estimate: null,
      signal_flips_per_year_shadow: null,
      note: 'Moderate turnover; FX spreads and overnight financing both apply and can eat much of the return.',
    },
    evidence: 'reference_only',
    last_verified: '2026-09-21',
    verification_note: MOCK_VERIFICATION_NOTE,
    retirement_rule: 'Retire if the net Sharpe over the last 36 months falls below zero.',
    evidence_declared: 'reference_only',
    evidence_basis: 'declared',
    blockers: [],
    admission: { status: 'admitted' },
    verification_kind: null,
  },
  {
    id: 'crypto-trend',
    version: 1,
    name: 'Crypto trend following (BTC and ETH)',
    sources: [
      'Liu, Y. and Tsyvinski, A. (2021). Risks and Returns of Cryptocurrency. The Review of Financial Studies.',
    ],
    rule_text:
      'Each day, hold BTC and ETH at equal weights only while each price is above its 100-day simple moving average. Otherwise keep that half in cash.',
    reference_implementation: 'crypto_sma100_trend 0.1.0 (mock reference; nothing is pinned).',
    instruments: ['BTC/USD', 'ETH/USD'],
    requirements: {
      needs_shorting: false,
      min_leverage_gross: 1,
      gross_exposure_reference: null,
      needs_portfolio_vol_target: false,
      max_abs_weight_per_instrument: 0.5,
      cross_asset_rebalance: 'preferred',
      asset_classes: ['crypto_spot'],
      venues_supported: ['kraken'],
      min_history_years: 0.3,
      notes: ['BTC and ETH are deep enough for startup-fund size.'],
    },
    rebalance: { kind: 'daily', detail: 'Each day at the UTC close.' },
    parameters: { sma_days: 100, weight_per_asset: 0.5 },
    expected_ranges: [
      // Like the real seed's crypto entry: a range with no known bounds, shown as such.
      { metric: 'sharpe_gross', low: null, high: null, role: 'performance_band', source: MOCK_SOURCE, note: MOCK_RANGE_NOTE },
      { metric: 'max_drawdown', low: null, high: 0.6, role: 'integrity_bound', source: MOCK_SOURCE, note: MOCK_RANGE_NOTE },
    ],
    backtest_window: { start: '2016-01-01', end: '2020-12-31', max_observed_drawdown: null },
    known_deviations: [],
    failure_regimes: [
      'Sideways markets that whipsaw around the average.',
      'Sharp overnight crashes that hit before the daily signal can react.',
    ],
    cost_profile: {
      turnover_per_year_estimate: null,
      signal_flips_per_year_shadow: null,
      note: 'A trade every few days on average. Exchange fees and spreads are much larger than for ETFs.',
    },
    evidence: 'reference_only',
    last_verified: '2026-09-21',
    verification_note: MOCK_VERIFICATION_NOTE,
    retirement_rule: 'Retire if the net Sharpe over the last 24 months falls below zero.',
    evidence_declared: 'reference_only',
    evidence_basis: 'declared',
    blockers: [],
    // Like the real seed's crypto_trend_100d: no performance band yet, so Held rather than Admitted.
    admission: { status: 'held', reasons: ['No performance-band low bound is set yet for this entry.'] },
    verification_kind: null,
  },
];

/** Numbers the mock proposal builder needs that a library entry does not carry. */
interface MockStats {
  vol: number;
  worstDrawdown: number;
  netSharpe: number;
  grossSharpe: number;
}

const MOCK_STATS: Record<string, MockStats> = {
  'etf-trend': { vol: 0.08, worstDrawdown: 0.15, netSharpe: 0.62, grossSharpe: 0.71 },
  'fx-momentum': { vol: 0.1, worstDrawdown: 0.2, netSharpe: 0.3, grossSharpe: 0.5 },
  'crypto-trend': { vol: 0.45, worstDrawdown: 0.35, netSharpe: 0.9, grossSharpe: 1.0 },
};

function statsOf(id: string): MockStats {
  return MOCK_STATS[id] ?? { vol: 0.2, worstDrawdown: 0.25, netSharpe: 0, grossSharpe: 0 };
}

// ---------------------------------------------------------------------------
// Eligibility: a mirror of strategy_library.rs::eligibility (deterministic)
// ---------------------------------------------------------------------------

/** What the platform can do today (mirror of Rust `current_platform_capabilities()`). */
export interface MockPlatformCapabilities {
  shorting_in_backtest: boolean;
  account_leverage: boolean;
  portfolio_vol_target: boolean;
  cross_asset_rebalance: boolean;
  per_instrument_weight_above_1: boolean;
  venues_live_ready: string[];
}

export const MOCK_PLATFORM: MockPlatformCapabilities = {
  shorting_in_backtest: true,
  account_leverage: true,
  portfolio_vol_target: false,
  cross_asset_rebalance: false,
  per_instrument_weight_above_1: false,
  venues_live_ready: ['kraken'],
};

/** Rust declares reason codes in this order and sorts the reasons by it. */
const REASON_ORDER: ReasonCode[] = [
  'shorting_not_permitted',
  'leverage_exceeds_cap',
  'gross_exceeds_exposure_limit',
  'asset_class_not_permitted',
  'instruments_not_allowed',
  'instrument_denied',
  'venue_not_permitted',
  'shorting_unsupported',
  'leverage_unsupported',
  'portfolio_vol_target_unsupported',
  'weight_above_one_unsupported',
  'cross_asset_rebalance_unsupported',
  'not_live_ready',
  'cross_asset_rebalance_approximated',
  'peak_gross_may_clip',
];

/** Each code has exactly one origin (Rust `ReasonCode::origin`). */
function originOf(code: ReasonCode): Reason['origin'] {
  const i = REASON_ORDER.indexOf(code);
  if (i <= REASON_ORDER.indexOf('venue_not_permitted')) return 'mandate';
  if (i <= REASON_ORDER.indexOf('not_live_ready')) return 'platform';
  return 'advisory';
}

const LEVERAGE_EPS = 1e-9;

/** True when `need` cannot be satisfied by `cap`; non-finite values fail closed. */
function exceeds(need: number, cap: number): boolean {
  return !Number.isFinite(need) || !Number.isFinite(cap) || need > cap + LEVERAGE_EPS;
}

/** Case-insensitive, ignoring `/`, `-`, `_` and spaces, so BTC/USD and btc-usd match. */
const normSymbol = (s: string): string => s.replace(/[/\-_ ]/g, '').toUpperCase();
const normLower = (s: string): string => s.trim().toLowerCase();
const quoted = (items: string[]): string => items.map((s) => `"${s}"`).join(', ');

/**
 * Whether a library entry fits a mandate (what the USER forbids) and the
 * platform (what it cannot do TODAY), with EVERY reason, each tagged with its
 * origin. Mirrors the pure Rust function rule for rule and message for
 * message, so the mock and the server disagree only in the entries.
 */
export function checkEligibility(
  entry: LibraryEntry,
  mandate: MandateBody,
  platform: MockPlatformCapabilities = MOCK_PLATFORM,
): Eligibility {
  const u = mandate.universe;
  const req = entry.requirements;
  const name = entry.name;
  const reasons: Reason[] = [];
  const push = (code: ReasonCode, blocks: ReasonBlocks, message: string) =>
    reasons.push({ code, origin: originOf(code), blocks, message });

  // ---- mandate
  if (req.needs_shorting && !u.shorting) {
    push('shorting_not_permitted', 'paper_and_live', `${name} takes short positions, but the mandate does not allow shorting.`);
  }
  if (exceeds(req.min_leverage_gross, u.leverage_max_gross)) {
    push(
      'leverage_exceeds_cap',
      'paper_and_live',
      `${name} needs about ${req.min_leverage_gross.toFixed(1)}x gross notional, but the mandate's leverage cap is ${u.leverage_max_gross.toFixed(1)}x.`,
    );
  }
  if (exceeds(req.min_leverage_gross, mandate.exposure.max_gross)) {
    push(
      'gross_exceeds_exposure_limit',
      'paper_and_live',
      `${name} needs about ${req.min_leverage_gross.toFixed(1)}x gross exposure, but the mandate's gross exposure limit is ${mandate.exposure.max_gross.toFixed(1)}x.`,
    );
  }
  const classes = new Set(u.asset_classes.map(normLower));
  const missingClasses = req.asset_classes.filter((c) => !classes.has(normLower(c)));
  if (missingClasses.length > 0) {
    push(
      'asset_class_not_permitted',
      'paper_and_live',
      `${name} trades ${quoted(missingClasses)}, which the mandate's asset classes do not include.`,
    );
  }
  const allow = new Set(u.instrument_allow.map(normSymbol));
  const deny = new Set(u.instrument_deny.map(normSymbol));
  const notAllowed = entry.instruments.filter((s) => !allow.has(normSymbol(s)));
  if (notAllowed.length > 0) {
    const which =
      notAllowed.length === entry.instruments.length
        ? 'none of them are'
        : `${notAllowed.length} of ${entry.instruments.length} are missing`;
    push(
      'instruments_not_allowed',
      'paper_and_live',
      `${name} needs all of its instruments, but ${which} in the mandate's allow-list; missing: ${quoted(notAllowed)}.`,
    );
  }
  const denied = entry.instruments.filter((s) => deny.has(normSymbol(s)));
  if (denied.length > 0) {
    push('instrument_denied', 'paper_and_live', `${name} needs ${quoted(denied)}, which the mandate explicitly denies.`);
  }
  const mandateVenues = new Set(u.venues.map(normLower));
  const permittedVenues = req.venues_supported.map(normLower).filter((v) => mandateVenues.has(v));
  if (permittedVenues.length === 0) {
    push(
      'venue_not_permitted',
      'paper_and_live',
      `${name} trades on ${quoted(req.venues_supported)}, but the mandate permits only ${quoted(u.venues)}.`,
    );
  }

  // ---- platform
  if (req.needs_shorting && !platform.shorting_in_backtest) {
    push('shorting_unsupported', 'paper_and_live', `${name} takes short positions, and the platform cannot short today.`);
  }
  if (req.min_leverage_gross > 1 + LEVERAGE_EPS && !platform.account_leverage) {
    push(
      'leverage_unsupported',
      'paper_and_live',
      `${name} needs about ${req.min_leverage_gross.toFixed(1)}x gross notional, and the platform has no account leverage today.`,
    );
  }
  if (req.needs_portfolio_vol_target && !platform.portfolio_vol_target) {
    push(
      'portfolio_vol_target_unsupported',
      'paper_and_live',
      `${name} sizes the whole sleeve to a joint portfolio volatility target, which the platform cannot express today (each asset is sized alone).`,
    );
  }
  const w = req.max_abs_weight_per_instrument;
  if (w != null && w > 1 + LEVERAGE_EPS && !platform.per_instrument_weight_above_1) {
    push(
      'weight_above_one_unsupported',
      'paper_and_live',
      `${name} can give a single instrument a weight of up to ${w.toFixed(1)}x sleeve equity, but the platform caps a per-instrument position size at 1.0 today.`,
    );
  }
  if (req.cross_asset_rebalance === 'needed' && !platform.cross_asset_rebalance) {
    push(
      'cross_asset_rebalance_unsupported',
      'paper_and_live',
      `${name} needs positions rebalanced jointly across instruments, which the platform cannot do today.`,
    );
  } else if (req.cross_asset_rebalance === 'preferred' && !platform.cross_asset_rebalance) {
    push(
      'cross_asset_rebalance_approximated',
      'nothing',
      `${name} rebalances to fixed weights across instruments; the platform runs each instrument as an independent sub-account, so weights drift between rebalances. Results approximate the published rule.`,
    );
  }
  if (permittedVenues.length > 0) {
    const liveReady = new Set(platform.venues_live_ready.map(normLower));
    if (!permittedVenues.some((v) => liveReady.has(v))) {
      push(
        'not_live_ready',
        'live_only',
        `Live trading is not available yet on the venue(s) ${name} can use under this mandate; it can run on paper only.`,
      );
    }
  }

  // ---- advisory
  const g = req.gross_exposure_reference;
  if (g) {
    const room = Math.min(u.leverage_max_gross, mandate.exposure.max_gross);
    if (!exceeds(req.min_leverage_gross, room) && Number.isFinite(room) && g.max > room + LEVERAGE_EPS) {
      push(
        'peak_gross_may_clip',
        'nothing',
        `The mandate allows up to ${room.toFixed(1)}x gross, but the reference run of ${name} needed up to ${g.max.toFixed(1)}x at its peak (typical ${g.median.toFixed(1)}x); in peak months the target would be clipped.`,
      );
    }
  }

  reasons.sort((a, b) => REASON_ORDER.indexOf(a.code) - REASON_ORDER.indexOf(b.code));
  const eligiblePaper = !reasons.some((r) => r.blocks === 'paper_and_live');
  const eligibleLive = eligiblePaper && !reasons.some((r) => r.blocks === 'live_only');
  return { eligible_paper: eligiblePaper, eligible_live: eligibleLive, reasons };
}

// ---------------------------------------------------------------------------
// Proposal builder: the stand-in for the agent (fixed, deterministic rule).
// Output has exactly the ProposalBody shape of program/src/proposals.rs.
// ---------------------------------------------------------------------------

export const SELECTION_RULE =
  "Pre-declared rule: consider every documented strategy. Keep only those the mandate and the platform allow, whose evidence is 'replicated on the platform', and for which the platform has a daily return history. Take at most 4, in order of entry id. Weight by inverse volatility over their common history. Keep each strategy's parameters exactly as published. Then scale all sizes so the worst fall in the replayed history stays within 90% of the first drawdown rung. (Mock: fixed illustrative statistics stand in for the return history.)";

/** At most this many sleeves in one proposal (same as the Rust builder). */
const MAX_SLEEVES = 4;
/** The blend is sized so its worst replayed fall stays within this share of rung 1. */
const LADDER_HEADROOM = 0.9;

const pct = (x: number): string => `${(x * 100).toFixed(1)}%`;

/** "Monthly, at month-end" / "Monthly" / "Daily", as the Rust builder labels them. */
function rebalanceLabel(e: LibraryEntry): string {
  if (e.rebalance.kind === 'daily') return 'Daily';
  return e.rebalance.detail.toLowerCase().includes('month-end') ? 'Monthly, at month-end' : 'Monthly';
}

/** The value the mock can honestly "measure" for a range metric; unknown metrics are not guessed. */
function measure(metric: string, st: MockStats): number | null {
  switch (metric) {
    case 'sharpe_net':
      return st.netSharpe;
    case 'sharpe_gross':
      return st.grossSharpe;
    case 'max_drawdown':
      return st.worstDrawdown;
    default:
      return null;
  }
}

const inRange = (r: ExpectedRange, v: number): boolean =>
  Number.isFinite(v) && (r.low === null || v >= r.low) && (r.high === null || v <= r.high);

function refusal(
  code: ProposalRefusalCode,
  message: string,
  considered: number,
  rejected: AlternativeRejected[],
): MockApiError {
  const details: ProposalRefusal = {
    error: message,
    code,
    candidates_considered: considered,
    alternatives_rejected: rejected,
  };
  return new MockApiError(409, message, details);
}

/**
 * Build a proposal for a mandate. Pure: same inputs, same output. Like the real
 * builder it REFUSES (a 409 with `code: 'no_eligible_strategies'` and every
 * candidate's reasons) when nothing passes, rather than returning an empty one.
 */
export function buildProposal(mandate: MandateRecord, library: LibraryEntry[], now: Date, id: string): Proposal {
  const body = mandate.body;
  const capital = Number(body.capital.allocated.amount);
  const rung1 = body.loss.drawdown_ladder[0];
  const considered = library.length;

  const rejected: AlternativeRejected[] = [];
  const passing: LibraryEntry[] = [];
  const ordered = [...library].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const entry of ordered) {
    const elig = checkEligibility(entry, body);
    if (!elig.eligible_paper) {
      rejected.push({ entry_id: entry.id, name: entry.name, reasons: paperBlockingReasons(elig).map((r) => r.message) });
    } else if (entry.evidence !== 'replicated_on_platform') {
      rejected.push({
        entry_id: entry.id,
        name: entry.name,
        reasons: [
          'It fits your mandate, but it has not been replicated on the platform yet. A strategy needs that before it can be proposed.',
        ],
      });
    } else {
      passing.push(entry);
    }
  }
  const chosen = passing.slice(0, MAX_SLEEVES);
  for (const extra of passing.slice(MAX_SLEEVES)) {
    rejected.push({ entry_id: extra.id, name: extra.name, reasons: [`Over the limit of ${MAX_SLEEVES} sleeves.`] });
  }

  if (chosen.length === 0) {
    throw refusal(
      'no_eligible_strategies',
      considered === 0
        ? 'The strategy library is empty, so there is nothing to propose yet.'
        : `None of the ${considered} documented strategies both fits your mandate and has passed every check, so there is nothing to propose yet.`,
      considered,
      rejected,
    );
  }

  const inv = chosen.map((e) => 1 / statsOf(e.id).vol);
  const invSum = inv.reduce((a, b) => a + b, 0);
  const sleeves: ProposalSleeve[] = chosen.map((e, i) => ({
    entry_id: e.id,
    entry_version: e.version,
    entry_hash: mockHash(e),
    name: e.name,
    weight: (inv[i] ?? 0) / invSum,
    evidence_label: e.evidence,
    expected_range: primaryRange(e),
    why: `Fits your mandate and is replicated on the platform (last verified ${e.last_verified}). Rule: ${e.rule_text}`,
    rebalance_frequency: rebalanceLabel(e),
  }));

  // Worst historical fall of the blend, assuming the sleeves fall together (a conservative bound).
  const worstUnscaled = sleeves.reduce((s, sl) => s + sl.weight * statsOf(sl.entry_id).worstDrawdown, 0);
  const firstRungAt = rung1?.at ?? 0.1;
  const riskScale =
    worstUnscaled > 0 ? Math.min(1, Math.round(((firstRungAt * LADDER_HEADROOM) / worstUnscaled) * 100) / 100) : 1;
  const worstScaled = Math.round(worstUnscaled * riskScale * 1000) / 1000;

  const period = 'the reference history (illustrative mock figures, nothing was actually replayed)';
  const replay: LadderReplay = {
    period,
    worst_drawdown_unscaled: Math.round(worstUnscaled * 1000) / 1000,
    risk_scale: riskScale,
    worst_drawdown_scaled: worstScaled,
    rungs: body.loss.drawdown_ladder.map((r, i) => ({
      rung: i + 1,
      at: r.at,
      action: r.action,
      scale: r.scale ?? null,
      reached_unscaled: worstUnscaled >= r.at,
      reached_scaled: worstScaled >= r.at,
    })),
  };

  // ---- replication check (the same items, in the same order, as the Rust builder)
  const items: ReplicationCheckItem[] = [];
  const limits: string[] = [
    'A backtest shows how the rule would have done in the past, not how it will do.',
    'The replay is one historical path at daily close-to-close resolution for a daily-rebalanced blend; real fills, timing and drift will differ.',
    'Position and order limits in your mandate are enforced when orders are placed, not checked in this replay.',
    'Nothing here is a promise of return.',
    'Mock mode: the figures above are illustrative stand-ins, not a replay of real history.',
  ];
  let outside = false;
  for (const e of chosen) {
    const st = statsOf(e.id);
    items.push({
      name: `${e.name}: replicated on the platform`,
      result: 'pass',
      detail: `Last verified ${e.last_verified}: ${e.verification_note}`,
    });
    items.push({
      name: `${e.name}: history covers the replay window`,
      result: 'pass',
      detail: 'Mock mode: illustrative statistics stand in for a real daily return history; none was replayed.',
    });
    const range = primaryRange(e);
    if (!range) {
      limits.push(`${e.name} publishes no expected range, so its results could not be checked against one.`);
      continue;
    }
    const v = measure(range.metric, st);
    if (v === null) {
      limits.push(
        `${e.name}: the platform cannot measure the range metric '${range.metric}' on this history, so that check was not made.`,
      );
    } else if (range.low === null && range.high === null) {
      limits.push(`${e.name}: its expected range on '${range.metric}' has no bounds, so it cannot fail (measured ${v.toFixed(2)}).`);
    } else {
      const ok = inRange(range, v);
      if (!ok) outside = true;
      const bounds =
        range.low !== null && range.high !== null
          ? `${range.low} to ${range.high}`
          : range.low !== null
            ? `at least ${range.low}`
            : `at most ${range.high}`;
      const role = range.role === 'performance_band' ? 'expected range' : 'integrity bound (outside it, suspect the test)';
      items.push({
        name: `${e.name}: inside the ${role}`,
        result: ok ? 'pass' : 'fail',
        detail: `Measured ${range.metric} = ${v.toFixed(3)}; the bounds are ${bounds}.`,
      });
    }
  }
  items.push({
    name: 'Sizing set explicitly',
    result: 'pass',
    detail: `Inverse-volatility weights, all sizes scaled by ${riskScale.toFixed(3)}.`,
  });
  const check: ReplicationCheck = {
    status: items.some((i) => i.result === 'fail') ? 'fail' : 'pass',
    outside_expected_range: outside,
    items,
  };

  const dollarsAtRung1 = rung1 ? formatUsd(capital * rung1.at) : '';
  const will = [...new Set(body.autonomy.may.map((a) => `${actionLabel(a)}, inside your limits`))];
  return {
    proposal_id: id,
    status: 'ready',
    mandate_id: mandate.mandate_id,
    mandate_version: mandate.version,
    created_at: now.toISOString(),
    capital: body.capital.allocated,
    generated_by: 'mock agent (fixed rule, no model called)',
    selection_rule: SELECTION_RULE,
    candidates_considered: considered,
    rationale: `Of ${considered} documented strategies, ${sleeves.length} fit your mandate and pass every check: ${sleeves
      .map((s) => s.name)
      .join(', ')}. Sizes are scaled by ${riskScale.toFixed(3)} so the worst fall in the replayed history (${pct(
      replay.worst_drawdown_unscaled,
    )} before scaling, ${pct(worstScaled)} after) stays inside your first ladder rung at ${pct(firstRungAt)}${
      dollarsAtRung1 ? ` (${dollarsAtRung1} at your capital)` : ''
    }.`,
    sleeves,
    alternatives_rejected: rejected,
    ladder_replay: replay,
    replication_check: check,
    expected_range: {
      metric: 'max_drawdown',
      low: null,
      high: worstScaled,
      role: 'performance_band',
      source: `Replay of the blend over ${period}, after risk scaling.`,
      note: 'The worst fall seen in that history, not a forecast: a worse fall can happen. Sharpe and return ranges are per sleeve and are not blended.',
    },
    costs_turnover: chosen.map((e) => `${e.name}: ${e.cost_profile.note}`).join(' '),
    will_do_without_asking: will,
    needs_approval: [
      'Approving this proposal (it starts a paper run, not live trading)',
      ...body.autonomy.needs_confirmation.map((a) => actionLabel(a)),
      'Going live (a separate consent step)',
    ],
    limits_of_evidence: limits,
    override_note: null,
    plan_id: null,
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface Store {
  mandates: MandateRecord[];
  proposals: Proposal[];
  plans: Plan[];
  seq: number;
}

const STORE_KEY = 'mendl.mock.mandate-store.v1';
/** The mock always knows its balance; the real API may return `equity: null`. */
export const MOCK_ACCOUNT: MandateAccountContext & { equity: Money } = {
  account_id: 'acct-alpaca-paper-4821',
  broker: 'alpaca',
  label: 'Alpaca paper account ending 4821',
  equity: { amount: '100000.00', ccy: 'USD' },
  paper: true,
};
const MOCK_USER = 'mock-user';

let store: Store | null = null;

function newId(prefix: string, s: Store): string {
  s.seq += 1;
  return `${prefix}-${s.seq.toString().padStart(4, '0')}`;
}

/** Non-cryptographic stand-in for the server's SHA-256 (FNV-1a 64-bit), clearly prefixed. */
export function mockHash(body: unknown): string {
  let h = 0xcbf29ce484222325n;
  const text = JSON.stringify(body);
  for (let i = 0; i < text.length; i += 1) {
    h ^= BigInt(text.charCodeAt(i));
    h = (h * 0x100000001b3n) & 0xffffffffffffffffn;
  }
  return `mock-${h.toString(16).padStart(16, '0')}`;
}

function activate(s: Store, record: MandateRecord, now: Date): void {
  for (const m of s.mandates) {
    if (m.account_id === record.account_id && m.status === 'active') m.status = 'superseded';
  }
  record.status = 'active';
  record.granted_by = MOCK_USER;
  record.granted_at = now.toISOString();
  record.effective_from = now.toISOString();
  record.review_by = new Date(now.getTime() + 90 * 24 * 3600 * 1000).toISOString();
  // A new mandate version invalidates open proposals (PLAN.md state machine).
  for (const p of s.proposals) {
    if (p.status === 'ready' || p.status === 'draft') p.status = 'expired';
  }
}

function seedStore(): Store {
  const s: Store = { mandates: [], proposals: [], plans: [], seq: 0 };
  const now = new Date();
  const form = { ...templateForm('balanced'), capital: '50000', jurisdiction: 'US-GA' };
  const { body } = formToBody(form, {
    by: MOCK_USER,
    at: now.toISOString(),
    docVersion: CURRENT_DOC_VERSION,
    acknowledged: ['own_capital_attestation', 'risk_disclosure'],
  });
  const record: MandateRecord = {
    mandate_id: newId('mandate', s),
    account_id: MOCK_ACCOUNT.account_id,
    version: 1,
    status: 'draft',
    body,
    body_hash: mockHash(body),
    created_by: 'user',
    granted_by: null,
    granted_at: null,
    effective_from: null,
    review_by: null,
    created_at: now.toISOString(),
  };
  s.mandates.push(record);
  activate(s, record, now);
  s.proposals.push(buildProposal(record, MOCK_LIBRARY, now, newId('proposal', s)));
  return s;
}

/** A proposal in the current wire shape (an older browser may hold one from before the shapes were aligned). */
function isCurrentProposal(p: unknown): boolean {
  const o = p as Partial<Proposal> | null;
  return (
    !!o &&
    typeof o.expected_range === 'object' &&
    o.expected_range !== null &&
    typeof o.expected_range.metric === 'string' &&
    Array.isArray(o.sleeves) &&
    o.sleeves.every((sl) => typeof sl.entry_hash === 'string')
  );
}

function isStore(x: unknown): x is Store {
  const o = x as Partial<Store> | null;
  return (
    !!o &&
    Array.isArray(o.mandates) &&
    Array.isArray(o.proposals) &&
    Array.isArray(o.plans) &&
    typeof o.seq === 'number' &&
    o.proposals.every(isCurrentProposal)
  );
}

function load(): Store {
  if (store) return store;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORE_KEY) : null;
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isStore(parsed)) {
        store = parsed;
        return store;
      }
    }
  } catch {
    /* private mode or corrupt value: fall through to the seed */
  }
  store = seedStore();
  save();
  return store;
}

function save(): void {
  try {
    if (store && typeof localStorage !== 'undefined') localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    /* storage unavailable: state stays in memory only */
  }
}

/** Forget everything and start again from the seed (tests and a "reset demo" affordance). */
export function resetMockBackend(): void {
  store = null;
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORE_KEY);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Request handling
// ---------------------------------------------------------------------------

function activeMandate(s: Store): MandateRecord | null {
  return s.mandates.find((m) => m.account_id === MOCK_ACCOUNT.account_id && m.status === 'active') ?? null;
}

/**
 * The mock has no scheduler and no `meta_portfolios` table, so it cannot honestly claim `pending` or
 * `provisioned` -- those would be guesses. `disabled` is also the truthful real-world default
 * (`MANDATE_PORTFOLIO_MERGE_ENABLED` is unset on every real deployment today), so it is the one answer
 * the mock can give without inventing platform state that doesn't exist here.
 */
function provisioningFor(active: MandateRecord | null): MandatePortfolioProvisioning | null {
  if (!active) return null;
  return { status: 'disabled', meta_portfolio_id: null };
}

function latestProposal(s: Store): Proposal | null {
  return [...s.proposals].sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0))[0] ?? null;
}

/** The `doc` ids a sign request carries (`{ acknowledgements: [{ doc, doc_version }] }`); [] when it has none. */
function signAcknowledgements(body: unknown): string[] {
  if (typeof body !== 'object' || body === null) return [];
  const acks = (body as { acknowledgements?: unknown }).acknowledgements;
  if (!Array.isArray(acks)) return [];
  return acks
    .map((a) => (typeof a === 'object' && a !== null ? (a as { doc?: unknown }).doc : undefined))
    .filter((d): d is string => typeof d === 'string');
}

function validationError(violations: Violation[]): MockApiError {
  return new MockApiError(422, 'The mandate did not pass validation.', {
    error: 'The mandate did not pass validation.',
    violations,
  });
}

function asRecord(v: unknown): Record<string, unknown> {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};
}

function createMandate(s: Store, payload: unknown): MandateRecord {
  const p = asRecord(payload);
  const accountId = typeof p.account_id === 'string' ? p.account_id : MOCK_ACCOUNT.account_id;
  if (accountId !== MOCK_ACCOUNT.account_id) throw new MockApiError(404, 'That broker account was not found.');
  const parsed = validateMandateJson(p.body);
  if (!parsed.ok) throw validationError(parsed.violations);

  // allocated <= broker equity at grant time; reject above 1.05x (SPEC section 2).
  const allocated = parseDecimal(parsed.body.capital.allocated.amount);
  const equity = parseDecimal(MOCK_ACCOUNT.equity.amount);
  if (allocated && equity) {
    const limit = { mant: equity.mant * 105n, scale: equity.scale + 2 };
    if (compareDec(allocated, limit) > 0) {
      throw validationError([
        { field: 'capital.allocated.amount', message: `is more than the account holds (${formatUsd(Number(MOCK_ACCOUNT.equity.amount))})` },
      ]);
    }
  }

  const now = new Date();
  // The server stamps who and when on every acknowledgement; a client value is only a claim.
  const body: MandateBody = {
    ...parsed.body,
    basis: {
      ...parsed.body.basis,
      acknowledgements: parsed.body.basis.acknowledgements.map((a) => ({ ...a, at: now.toISOString(), by: MOCK_USER })),
    },
  };
  const version = Math.max(0, ...s.mandates.filter((m) => m.account_id === accountId).map((m) => m.version)) + 1;
  const record: MandateRecord = {
    mandate_id: newId('mandate', s),
    account_id: accountId,
    version,
    status: 'draft',
    body,
    body_hash: mockHash(body),
    created_by: 'user',
    granted_by: null,
    granted_at: null,
    effective_from: null,
    review_by: null,
    created_at: now.toISOString(),
  };
  s.mandates.push(record);
  return record;
}

/**
 * Answer one mock request. `endpoint` includes the query string, exactly as the
 * API client passes it. Throws MockApiError for 4xx responses.
 */
export function handleMockRequest(endpoint: string, method: string, body?: unknown): unknown {
  const [path = '', query = ''] = endpoint.split('?');
  const params = new URLSearchParams(query);
  const parts = path.split('/').filter(Boolean); // ['api', 'mandates', ...]
  const s = load();
  const m = method.toUpperCase();

  // ---- /api/mandates
  if (parts[1] === 'mandates') {
    if (parts[2] === 'context' && m === 'GET') return { accounts: [MOCK_ACCOUNT] };
    if (parts[2] === 'active' && m === 'GET') {
      const active = activeMandate(s);
      return { mandate: active, portfolio_provisioning: provisioningFor(active) };
    }
    if (parts.length === 2 && m === 'GET') {
      const list = [...s.mandates].sort((a, b) => b.version - a.version);
      return { mandates: list, total: list.length };
    }
    if (parts.length === 2 && m === 'POST') {
      const record = createMandate(s, body);
      save();
      return record;
    }
    if (parts.length === 4 && parts[3] === 'sign' && m === 'POST') {
      // Same contract as the server's `sign_mandate` (observed live 2026-10-15):
      // no JSON body at all is a 400 from the Json extractor, and the required
      // acknowledgements must be in THIS request (`{}` is a 422 naming each
      // missing doc) -- the draft's stored `basis.acknowledgements` do not count.
      if (body === undefined) {
        throw new MockApiError(400, 'Failed to parse the request body as JSON: EOF while parsing a value at line 1 column 0');
      }
      const submitted = signAcknowledgements(body);
      const record = s.mandates.find((x) => x.mandate_id === parts[2]);
      if (!record) throw new MockApiError(404, 'Mandate not found.');
      if (record.status !== 'draft') throw new MockApiError(409, 'Only a draft mandate can be signed.');
      // Re-validate at signing: the signature covers exactly what passes the rules.
      const parsed = validateMandateJson(record.body);
      if (!parsed.ok) throw validationError(parsed.violations);
      const missing = REQUIRED_ACK_DOCS.filter((d) => !submitted.includes(d));
      if (missing.length > 0) {
        throw validationError(
          missing.map((d) => ({ field: 'basis.acknowledgements', message: `missing required acknowledgement: ${d}` })),
        );
      }
      activate(s, record, new Date());
      save();
      return record;
    }
    if (parts.length === 3 && m === 'GET') {
      const record = s.mandates.find((x) => x.mandate_id === parts[2]);
      if (!record) throw new MockApiError(404, 'Mandate not found.');
      return record;
    }
  }

  // ---- /api/library/cells, /api/library/families (R41: empty until a cell is committed)
  if (parts[1] === 'library' && parts.length === 3 && m === 'GET') {
    if (parts[2] === 'cells') return { cells: [] };
    if (parts[2] === 'families') return { families: [] };
  }

  // ---- /api/library
  if (parts[1] === 'library' && parts.length === 2 && m === 'GET') {
    if (params.get('for') === 'active-mandate') {
      const active = activeMandate(s);
      if (!active) {
        const res: LibraryResponse = {
          entries: MOCK_LIBRARY,
          mandate_version: null,
          notice: 'You have no active mandate yet, so eligibility cannot be checked.',
        };
        return res;
      }
      const entries: LibraryEntryView[] = MOCK_LIBRARY.map((e) => ({ ...e, eligibility: checkEligibility(e, active.body) }));
      const res: LibraryResponse = { entries, mandate_version: active.version };
      return res;
    }
    const res: LibraryResponse = { entries: MOCK_LIBRARY };
    return res;
  }

  // ---- /api/proposals
  if (parts[1] === 'proposals') {
    if (parts.length === 2 && m === 'GET') {
      const list = [...s.proposals].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      return { proposals: list, total: list.length };
    }
    if (parts[2] === 'latest' && m === 'GET') {
      const p = latestProposal(s);
      return { proposal: p, plan: p?.plan_id ? (s.plans.find((x) => x.plan_id === p.plan_id) ?? null) : null };
    }
    if (parts.length === 2 && m === 'POST') {
      const active = activeMandate(s);
      if (!active) {
        const message = 'You need an active mandate before the agent can propose anything.';
        throw new MockApiError(409, message, { error: message, code: 'no_active_mandate' });
      }
      const proposal = buildProposal(active, MOCK_LIBRARY, new Date(), newId('proposal', s));
      s.proposals.push(proposal);
      save();
      return proposal;
    }
    if (parts.length === 3 && m === 'GET') {
      const p = s.proposals.find((x) => x.proposal_id === parts[2]);
      if (!p) throw new MockApiError(404, 'Proposal not found.');
      return { proposal: p, plan: p.plan_id ? (s.plans.find((x) => x.plan_id === p.plan_id) ?? null) : null };
    }
    if (parts.length === 4 && parts[3] === 'approve' && m === 'POST') {
      const p = s.proposals.find((x) => x.proposal_id === parts[2]);
      if (!p) throw new MockApiError(404, 'Proposal not found.');
      if (p.status !== 'ready') throw new MockApiError(409, `This proposal is ${p.status} and cannot be approved.`, { code: `proposal_${p.status}` });
      const active = activeMandate(s);
      if (!active || active.mandate_id !== p.mandate_id) {
        p.status = 'expired';
        save();
        throw new MockApiError(409, 'Your mandate changed after this proposal was made. Ask the agent to check it again.', { code: 'mandate_changed' });
      }
      const note = typeof asRecord(body).override_note === 'string' ? String(asRecord(body).override_note).trim() : '';
      if (p.replication_check.status === 'fail' && note.length < 10) {
        throw new MockApiError(409, 'The replication check failed. Approving needs a written override note (at least 10 characters).', { code: 'override_required' });
      }
      const now = new Date();
      const plan: Plan = {
        plan_id: newId('plan', s),
        proposal_id: p.proposal_id,
        mandate_id: active.mandate_id,
        mandate_version: active.version,
        mode: 'paper',
        state: 'paper',
        reconciliation: {
          status: 'awaiting_first_run',
          detail: 'Paper fills are compared with expected fills after each rebalance. The first check runs after the next scheduled rebalance.',
          last_checked: null,
        },
        created_at: now.toISOString(),
      };
      s.plans.push(plan);
      p.status = 'approved';
      p.plan_id = plan.plan_id;
      p.override_note = note || null;
      save();
      const res: ApproveResponse = { proposal: p, plan };
      return res;
    }
    if (parts.length === 4 && parts[3] === 'reject' && m === 'POST') {
      const p = s.proposals.find((x) => x.proposal_id === parts[2]);
      if (!p) throw new MockApiError(404, 'Proposal not found.');
      if (p.status !== 'ready') throw new MockApiError(409, `This proposal is ${p.status} and cannot be rejected.`);
      p.status = 'rejected';
      save();
      return { proposal: p };
    }
  }

  throw new MockApiError(404, `No mock route for ${m} ${endpoint}`);
}
